// @ts-nocheck -- staged migration: add explicit types before removing this directive.
import React, { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import Header from "../header/HeaderComponent";
import Footer from "../footer/FooterComponent";
import PracticeProgress from "../PracticeProgress/PracticeProgress";
import ProgressGate from "../ProgressGate/ProgressGate";
import StrokeOrderDiagram from "../StrokeOrderDiagram/StrokeOrderDiagramComponent";
import SpeakButton from "../SpeakButton/SpeakButtonComponent";
import { hiraganaWritingList, katakanaWritingList } from "../../consts/kanaWritingList";
import { useKanaDrawCounts } from "../../hooks/useKanaDrawCounts";
import { useKanaListenCounts } from "../../hooks/useKanaListenCounts";
import { useAuth } from "../../context/AuthContext";
import Seo from "../Seo/SeoComponent";
import * as analytics from "../../utils/analytics";
import styles from "./kanaWritingPracticePage.module.scss";
import BackLink from "../ui/BackLink/BackLink";
import { useLocalizedPath } from "../../i18n/localizedPath";

const scriptKeys = [
    { key: "hiragana", list: hiraganaWritingList },
    { key: "katakana", list: katakanaWritingList },
];

function KanaWritingPracticePage() {
    const { t, i18n } = useTranslation("kanaWritingPracticePage");
    const lp = useLocalizedPath();
    const { status } = useAuth();
    const { search } = useLocation();
    const initialScriptKey = new URLSearchParams(search).get("script") === "katakana" ? "katakana" : "hiragana";
    const [scriptKey, setScriptKey] = useState(initialScriptKey);
    const activeScript = scriptKeys.find((script) => script.key === scriptKey);
    const [selected, setSelected] = useState(() => scriptKeys.find((script) => script.key === initialScriptKey).list[0]);
    const [autoStartQuiz, setAutoStartQuiz] = useState(false);
    const { counts: drawCounts, increment: incrementDrawCount, syncStatus } = useKanaDrawCounts();
    const isProgressReady = syncStatus !== "loading";
    const drawCount = drawCounts[selected.symbol] || 0;

    const isPt = i18n.language === "pt";
    const currentPath = isPt ? "/pt/kana-writing-practice" : "/kana-writing-practice";

    let drawCountMessage = t("checkingDrawHistory");
    if (isProgressReady && drawCount > 0) {
        drawCountMessage = t("drawCount", { count: drawCount });
    } else if (isProgressReady) {
        drawCountMessage = t("noDrawsYet");
    }

    const { counts: listenCounts, increment: incrementListenCount, syncStatus: listenSyncStatus } = useKanaListenCounts();
    const isListenProgressReady = listenSyncStatus !== "loading";
    const listenCount = listenCounts[selected.symbol] || 0;

    let listenCountMessage = t("checkingListenHistory");
    if (isListenProgressReady && listenCount > 0) {
        listenCountMessage = t("listenCount", { count: listenCount });
    } else if (isListenProgressReady) {
        listenCountMessage = t("noListensYet");
    }

    function selectScript(key) {
        setScriptKey(key);
        setSelected(scriptKeys.find((script) => script.key === key).list[0]);
        setAutoStartQuiz(false);
    }

    const practiceStartedAtRef = useRef(null);

    function selectTile(item) {
        setSelected(item);
        setAutoStartQuiz(true);
    }

    function handlePracticeStart() {
        practiceStartedAtRef.current = Date.now();
        analytics.track("kana_practice_started", { symbol: selected.symbol, script: scriptKey });
    }

    function handlePracticeComplete(summary) {
        if (summary.totalMistakes !== 0) return;
        incrementDrawCount(selected.symbol);
        const durationSeconds = practiceStartedAtRef.current
            ? Math.round((Date.now() - practiceStartedAtRef.current) / 1000)
            : null;
        analytics.track("kana_practice_completed", {
            symbol: selected.symbol,
            script: scriptKey,
            duration_seconds: durationSeconds,
        });
    }

    function handlePlaySound() {
        incrementListenCount(selected.symbol);
        analytics.track("audio_played", { context: "kana_writing_practice", symbol: selected.symbol });
    }

    return (
        <div className={styles.page}>
            <Seo
                title={t("seoTitle")}
                description={t("seoDescription")}
                path={currentPath}
                lang={isPt ? "pt-PT" : undefined}
            />
            <Header />
            <main className={styles.main}>
                <section className={styles.container}>
                    <BackLink to={lp("/#start-learning")} label={t("common:backLink.toPractice")} />
                    <h1 className={styles.title}>{t("title")}</h1>
                    <p className={styles.description}>{t("description")}</p>

                    <div className={styles.scriptToggle}>
                        {scriptKeys.map((script) => (
                            <button
                                key={script.key}
                                type="button"
                                className={scriptKey === script.key ? styles.scriptButtonActive : styles.scriptButton}
                                onClick={() => selectScript(script.key)}
                            >
                                {t(script.key)}
                            </button>
                        ))}
                    </div>

                    <div className={styles.practicePanel}>
                        <div className={styles.practiceHeader}>
                            <span className={styles.practiceGlyph}>{selected.symbol}</span>
                            <span className={styles.practiceRomaji}>{selected.romaji}</span>
                            <SpeakButton
                                text={selected.symbol}
                                label={t("playPronunciation", { romaji: selected.romaji })}
                                className={styles.speakButtonLarge}
                                onPlay={handlePlaySound}
                            />
                            <SpeakButton
                                text={selected.symbol}
                                label={t("playSlowPronunciation", { romaji: selected.romaji })}
                                className={styles.speakButtonLarge}
                                onPlay={handlePlaySound}
                                slow
                            />
                        </div>
                        <StrokeOrderDiagram
                            key={selected.symbol}
                            symbol={selected.symbol}
                            speakText={selected.symbol}
                            previewAndDrawImmediately
                            startQuizImmediately={autoStartQuiz}
                            onQuizStart={handlePracticeStart}
                            onQuizComplete={handlePracticeComplete}
                            onPlaySound={handlePlaySound}
                            perfectMessage={status === "authenticated" ? t("perfectMessage") : undefined}
                        />
                        <ProgressGate message={t("logInToTrackPractice")}>
                            <PracticeProgress count={drawCount} label={t("practiceThisKana")} showLevel />
                            <p className={styles.drawCount}>{drawCountMessage}</p>
                            <p className={styles.drawCount}>{listenCountMessage}</p>
                        </ProgressGate>
                    </div>

                    <div className={styles.card}>
                        <h2 className={styles.cardTitle}>{t("charactersHeading", { script: t(activeScript.key) })}</h2>
                        <div className={styles.grid}>
                            {activeScript.list.map((item) => (
                                <button
                                    key={item.symbol}
                                    type="button"
                                    className={
                                        item.symbol === selected.symbol ? styles.gridTileActive : styles.gridTile
                                    }
                                    onClick={() => selectTile(item)}
                                >
                                    <span className={styles.gridGlyph}>{item.symbol}</span>
                                    <span className={styles.gridRomaji}>{item.romaji}</span>
                                </button>
                            ))}
                        </div>
                    </div>
                </section>
            </main>
            <Footer />
        </div>
    );
}

export default KanaWritingPracticePage;
